import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Typography, Paper, Grid, TextField, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Button, Chip, Stack, Card, Divider
} from "@mui/material";
import { Search, Print, Call, LocationOn } from "@mui/icons-material";
import { collection, getDocs, query, where, doc, getDoc, orderBy } from "firebase/firestore";
import { db, auth } from "../../firebase";
import Swal from "sweetalert2";

function Pendingfees() {
  const [loading, setLoading] = useState(true);
  const [schoolId, setSchoolId] = useState(null);
  const [academicYear, setAcademicYear] = useState(null);
  const [courses, setCourses] = useState([]);
  const [studentTypes, setStudentTypes] = useState([]);
  const [pendingReportData, setPendingReportData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);

  // Filters
  const [filterClass, setFilterClass] = useState("All");
  const [filterStudentType, setFilterStudentType] = useState("All");
  const [filterInstallment, setFilterInstallment] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    initFetch();
  }, []);

  const initFetch = async () => {
    setLoading(true);
    try {
      const userSnap = await getDoc(doc(db, "users", auth.currentUser.uid));
      const sId = userSnap.data()?.schoolId;
      setSchoolId(sId);

      const yearSnap = await getDocs(
        query(collection(db, "academicyears"), where("schoolId", "==", sId), where("status", "==", "Active"))
      );
      const activeYearName = yearSnap.empty ? null : yearSnap.docs[0].data().name;
      setAcademicYear(activeYearName);

      if (sId) {
        await fetchData(sId, activeYearName);
      }
    } catch (e) {
      console.error("Initialization Error:", e);
      Swal.fire("Error", "Failed to load initial configuration", "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (sId, year) => {
    try {
      const courseQuery = query(collection(db, "courses"), where("schoolId", "==", sId), orderBy("sequence", "asc"));
      
      let typesList = [];
      try {
        const typeSnap = await getDocs(query(collection(db, "studenttypes"), where("schoolId", "==", sId)));
        typesList = typeSnap.docs.map(d => d.data().type || d.data().name).filter(Boolean);
      } catch (err) {
        console.warn("Could not fetch studenttypes collection, using defaults.");
      }
      setStudentTypes(typesList.length > 0 ? typesList : ["Regular", "New"]);

      const [courseSnap, admSnap, studentSnap, structureSnap] = await Promise.all([
        getDocs(courseQuery),
        getDocs(query(collection(db, "admission"), where("schoolId", "==", sId), where('status', "==", 'Active'))),
        getDocs(query(collection(db, "students"), where("schoolId", "==", sId))),
        getDocs(query(collection(db, "fee_structures"), where("schoolId", "==", sId)))
      ]);

      const courseMap = {};
      const classList = [];
      courseSnap.forEach(d => {
        const data = d.data();
        courseMap[d.id] = data.name;
        classList.push({ id: d.id, name: data.name });
      });
      setCourses(classList);

      const studentMap = {};
      studentSnap.docs.forEach(d => { studentMap[d.id] = d.data(); });

      const structureMap = {};
      structureSnap.docs.forEach(d => {
        const data = d.data();
        const key = data.admissionId || data.classId;
        if (key) structureMap[key] = data;
      });

      const reportList = [];

      admSnap.docs.forEach(docSnap => {
        const adm = { id: docSnap.id, ...docSnap.data() };
        const sData = studentMap[adm.studentId] || {};
        
        const fullName = `${sData.firstname || ''} ${sData.fathername || ''} ${sData.surname || ''}`.trim().toUpperCase();
        const className = courseMap[adm.classId || adm.class] || 'N/A';
        const studentType = adm.studenttype || sData.studenttype || "Regular";
        const primaryMobile = adm.father_mobile1 || adm.father_mobile || sData.fatherMobile || sData.mobile || adm.mobile || "-";

        const feeStruct = structureMap[adm.id] || structureMap[adm.classId || adm.class] || { 
          heads: [], discounts: [], paymentHistory: [], normalInstallments: [], headWiseInstallments: {} 
        };

        const totalFees = (feeStruct.heads || []).filter(h => h.checked !== false).reduce((sum, h) => sum + (parseFloat(h.amount) || 0), 0);
        const totalDiscount = (feeStruct.discounts || []).reduce((sum, d) => sum + (parseFloat(d.amount) || 0), 0);
        const totalPaid = (feeStruct.paymentHistory || []).reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);
        const balanceDue = totalFees - totalDiscount - totalPaid;

        let allInstallments = [];
        if (Array.isArray(feeStruct.normalInstallments) && feeStruct.normalInstallments.length > 0) {
          allInstallments = feeStruct.normalInstallments.map((ins, i) => ({ 
            ...ins, 
            index: i, 
            name: ins.name || `Installment ${i + 1}` 
          }));
        } else if (feeStruct.headWiseInstallments) {
          Object.values(feeStruct.headWiseInstallments).forEach(instArr => {
            if (Array.isArray(instArr)) {
              instArr.forEach((ins, i) => allInstallments.push({ 
                ...ins, 
                index: i, 
                name: ins.name || `Installment ${i + 1}` 
              }));
            }
          });
        }

        if (balanceDue > 0) {
          reportList.push({
            admissionId: adm.id,
            studentId: adm.studentId,
            fullName,
            className,
            classId: adm.classId || adm.class,
            studentType,
            mobile: primaryMobile,
            totalFees,
            totalDiscount,
            totalPaid,
            balanceDue,
            installments: allInstallments,
            paymentHistory: feeStruct.paymentHistory || []
          });
        }
      });

      setPendingReportData(reportList);
      setFilteredData(reportList);
    } catch (e) {
      console.error("Report Fetch Error:", e);
      Swal.fire("Error", "Failed to compile pending fee records", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (cls, type, inst, search) => {
    setFilterClass(cls);
    setFilterStudentType(type);
    setFilterInstallment(inst);
    setSearchQuery(search);

    const term = search.toLowerCase().trim();

    const filtered = pendingReportData.filter(item => {
      const matchClass = cls === "All" || item.classId === cls;
      const matchType = type === "All" || item.studentType.toLowerCase() === type.toLowerCase();
      const matchSearch = item.fullName.toLowerCase().includes(term) || item.mobile.toLowerCase().includes(term);

      let matchInst = true;
      if (inst !== "All") {
        const targetIndex = String(inst);
        matchInst = item.installments.some((ins, index) => {
          const idxStr = String(ins.index !== undefined ? ins.index : index);
          if (idxStr !== targetIndex) return false;

          const instAmt = parseFloat(ins.amount || ins.finalAmount || 0);
          const paidForInst = item.paymentHistory
            .filter(p => String(p.installmentIndex) === targetIndex)
            .reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);

          return (instAmt - paidForInst) > 0;
        });
      }

      return matchClass && matchType && matchSearch && matchInst;
    });

    setFilteredData(filtered);
  };

  const getInstallmentBreakdown = (item) => {
    if (filterInstallment === "All") {
      return {
        amount: item.totalFees,
        paid: item.totalPaid,
        due: item.balanceDue
      };
    }

    const targetIndex = String(filterInstallment);
    let instAmount = 0;
    item.installments.forEach((ins, index) => {
      const idxStr = String(ins.index !== undefined ? ins.index : index);
      if (idxStr === targetIndex) {
        instAmount += parseFloat(ins.amount || ins.finalAmount || 0);
      }
    });

    const paidForInst = item.paymentHistory
      .filter(p => String(p.installmentIndex) === targetIndex)
      .reduce((sum, p) => sum + (parseFloat(p.paidAmount) || 0), 0);

    return {
      amount: instAmount,
      paid: paidForInst,
      due: Math.max(0, instAmount - paidForInst)
    };
  };

  const totalSummary = useMemo(() => {
    return filteredData.reduce((acc, curr) => {
      const breakdown = getInstallmentBreakdown(curr);
      acc.fees += breakdown.amount;
      acc.paid += breakdown.paid;
      acc.due += breakdown.due;
      acc.discount += curr.totalDiscount;
      return acc;
    }, { fees: 0, discount: 0, paid: 0, due: 0 });
  }, [filteredData, filterInstallment]);

  const handlePrint = () => {
    if (loading) return;
    setTimeout(() => {
      window.print();
    }, 300);
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" alignItems="center" height="80vh"><CircularProgress /></Box>;
  }

  return (
    <Box
      p={1}
      bgcolor="#f4f6f9"
      minHeight="100vh"
      sx={{
        "@media print": {
          bgcolor: "#fff",
          p: 0,
          m: 0,
          width: "100% !important",
          minWidth: "100% !important",
          "@page": { margin: "5mm", size: "auto" },
          ".no-print": { display: "none !important" },
          ".print-area": {
            p: 1,
            m: 0,
            width: "100% !important",
            maxWidth: "100% !important",
            boxShadow: "none !important",
            border: "none !important",
            display: "block !important"
          }
        }
      }}
    >
      {/* Print Stylesheet Injection */}
      <style>
        {`
          @media print {
            body, html {
              background-color: #ffffff !important;
              color: #000000 !important;
              font-size: 11px !important;
            }
            .no-print, button, header, nav {
              display: none !important;
            }
            .print-area, .MuiTableContainer-root, .MuiPaper-root {
              display: block !important;
              width: 100% !important;
              max-width: 100% !important;
              box-shadow: none !important;
              border: none !important;
              background: transparent !important;
              overflow: visible !important;
            }
            table {
              width: 100% !important;
              border-collapse: collapse !important;
            }
            tr {
              page-break-inside: avoid !important;
            }
            th, td {
              border-bottom: 1px solid #cbd5e1 !important;
              padding: 6px 8px !important;
              color: #000000 !important;
            }
          }
        `}
      </style>

      {/* Top Control Panel */}
      <Stack className="no-print" direction="row" justifyContent="space-between" alignItems="center" mb={2} px={1}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="text.primary">Pending Fees Report</Typography>
          <Typography variant="caption" color="text.secondary">Comprehensive tracking of all outstanding student balances</Typography>
        </Box>
        <Button
          variant="contained"
          size="small"
          startIcon={<Print />}
          disabled={loading}
          onClick={handlePrint}
          sx={{ borderRadius: 2, height: 34, fontSize: '12.5px', textTransform: 'none', px: 2, fontWeight: 700, boxShadow: 'none', bgcolor: '#1e293b' }}
        >
          Print Report
        </Button>
      </Stack>

      {/* Summary Stat Cards */}
      <Grid container spacing={2} mb={3} className="no-print">
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Typography variant="caption" color="text.secondary" fontWeight="bold">
              {filterInstallment === "All" ? "TOTAL ASSIGNED FEES" : `INSTALLMENT ${parseInt(filterInstallment) + 1} AMOUNT`}
            </Typography>
            <Typography variant="h6" fontWeight="800" color="#334155">₹{totalSummary.fees.toLocaleString()}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Typography variant="caption" color="error" fontWeight="bold">TOTAL DISCOUNTS</Typography>
            <Typography variant="h6" fontWeight="800" color="error">-₹{totalSummary.discount.toLocaleString()}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff' }}>
            <Typography variant="caption" color="success.main" fontWeight="bold">TOTAL COLLECTED</Typography>
            <Typography variant="h6" fontWeight="800" color="success.main">₹{totalSummary.paid.toLocaleString()}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card variant="outlined" sx={{ p: 2, textAlign: 'center', bgcolor: '#fff1f2', borderColor: '#fecdd3' }}>
            <Typography variant="caption" color="#b22222" fontWeight="bold">TOTAL PENDING BALANCE</Typography>
            <Typography variant="h6" fontWeight="900" color="#b22222">₹{totalSummary.due.toLocaleString()}</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Filter Toolbar */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0' }} className="no-print">
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              select
              size="small"
              label="Filter by Class"
              fullWidth
              value={filterClass}
              onChange={(e) => handleFilterChange(e.target.value, filterStudentType, filterInstallment, searchQuery)}
            >
              <MenuItem value="All">All Classes</MenuItem>
              {courses.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              select
              size="small"
              label="Student Type"
              fullWidth
              value={filterStudentType}
              onChange={(e) => handleFilterChange(filterClass, e.target.value, filterInstallment, searchQuery)}
            >
              <MenuItem value="All">All Types</MenuItem>
              {studentTypes.map((type, idx) => (
                <MenuItem key={idx} value={type}>{type}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12} sm={2}>
            <TextField
              select
              size="small"
              label="Installment Wise"
              fullWidth
              value={filterInstallment}
              onChange={(e) => handleFilterChange(filterClass, filterStudentType, e.target.value, searchQuery)}
            >
              <MenuItem value="All">All Installments</MenuItem>
              <MenuItem value="0">Installment 1 (Confirmation)</MenuItem>
              <MenuItem value="1">Installment 2</MenuItem>
              <MenuItem value="2">Installment 3</MenuItem>
              <MenuItem value="3">Installment 4</MenuItem>
            </TextField>
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              size="small"
              fullWidth
              placeholder="Search by student name or mobile..."
              value={searchQuery}
              onChange={(e) => handleFilterChange(filterClass, filterStudentType, filterInstallment, e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'action.active', mr: 1 }} />
              }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Main Printable Card Sheet */}
      <Card className="print-area" sx={{ p: 3, maxWidth: 1000, mx: 'auto', borderRadius: 3, boxShadow: "0 4px 12px rgba(0,0,0,0.03)", border: "1px solid #e2e8f0", bgcolor: "#fff" }}>
        
        {/* BRANDING HEADER BLOCK */}
        <Box sx={{ borderBottom: '2px solid #0f172a', pb: 2, mb: 2.5 }}>
          <Grid container justifyContent="space-between" alignItems="center">
            <Grid item xs={8}>
              <Typography variant="h5" fontWeight={900} color="text.primary" sx={{ letterSpacing: '-0.5px', textTransform: 'uppercase', lineHeight: 1.1 }}>
                Mohan Mali International School
              </Typography>
              <Stack direction="row" spacing={2} mt={1} color="text.secondary" flexWrap="wrap">
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', fontSize: '11.5px' }}>
                  <LocationOn sx={{ fontSize: 14, mr: 0.5, color: 'primary.main' }} /> At Alkud, Kavathe Mahankal, Sangli
                </Typography>
                <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', fontSize: '11.5px' }}>
                  <Call sx={{ fontSize: 14, mr: 0.5, color: 'primary.main' }} /> +91 9890555478
                </Typography>
              </Stack>
            </Grid>
            <Grid item xs={4} textAlign="right">
              <Box sx={{ display: 'inline-block', bgcolor: '#f1f5f9', px: 2, py: 1, borderRadius: 2, textAlign: 'right' }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', display: 'block', fontSize: '10px' }}>
                  {new Date().toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}
                </Typography>
                <Typography variant="subtitle2" fontWeight={800} color="primary.dark">PENDING FEES REPORT</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* Main Report Table (No Pagination - All Rows Displayed) */}
        <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Sr No</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Student Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Class</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Type</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Mobile No</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>
                  {filterInstallment === "All" ? "Total Fees" : "Installment Amount"}
                </TableCell>
                {filterInstallment === "All" && (
                  <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Discount</TableCell>
                )}
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1 }}>Paid</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, fontSize: '11.5px', py: 1, color: '#b22222' }}>Balance Due</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredData.length > 0 ? (
                filteredData.map((row, idx) => {
                  const breakdown = getInstallmentBreakdown(row);
                  return (
                    <TableRow key={row.admissionId} hover>
                      <TableCell sx={{ py: 0.8, fontSize: '12px' }}>{idx + 1}</TableCell>
                      <TableCell sx={{ py: 0.8, fontSize: '12px', fontWeight: 700 }}>{row.fullName}</TableCell>
                      <TableCell sx={{ py: 0.8, fontSize: '12px' }}><Chip label={row.className} size="small" variant="outlined" /></TableCell>
                      <TableCell sx={{ py: 0.8, fontSize: '12px' }}>
                        <Chip
                          label={row.studentType}
                          size="small"
                          color={row.studentType.toLowerCase() === 'new' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 0.8, fontSize: '12px' }}>{row.mobile}</TableCell>
                      <TableCell align="right" sx={{ py: 0.8, fontSize: '12px' }}>₹{breakdown.amount.toLocaleString()}</TableCell>
                      {filterInstallment === "All" && (
                        <TableCell align="right" sx={{ py: 0.8, fontSize: '12px', color: 'error.main' }}>-₹{row.totalDiscount.toLocaleString()}</TableCell>
                      )}
                      <TableCell align="right" sx={{ py: 0.8, fontSize: '12px', color: 'success.main' }}>₹{breakdown.paid.toLocaleString()}</TableCell>
                      <TableCell align="right" sx={{ py: 0.8, fontSize: '12px' }}>
                        <Typography variant="body2" fontWeight="900" sx={{ color: '#b22222', bgcolor: '#fff1f2', py: 0.2, px: 1, borderRadius: 1, display: 'inline-block' }}>
                          ₹{breakdown.due.toLocaleString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={filterInstallment === "All" ? 9 : 8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">No pending fee records match the selected criteria.</Typography>
                  </TableCell>
                </TableRow>
              )}

              {/* TOTAL SUMMARY FOOTER ROW */}
              {filteredData.length > 0 && (
                <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                  <TableCell colSpan={5} sx={{ fontWeight: 800, fontSize: '12.5px', py: 1.2 }}>
                    TOTAL ({filteredData.length} Students)
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '12.5px', py: 1.2 }}>
                    ₹{totalSummary.fees.toLocaleString()}
                  </TableCell>
                  {filterInstallment === "All" && (
                    <TableCell align="right" sx={{ fontWeight: 800, fontSize: '12.5px', py: 1.2, color: 'error.main' }}>
                      -₹{totalSummary.discount.toLocaleString()}
                    </TableCell>
                  )}
                  <TableCell align="right" sx={{ fontWeight: 800, fontSize: '12.5px', py: 1.2, color: 'success.dark' }}>
                    ₹{totalSummary.paid.toLocaleString()}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 900, fontSize: '12.5px', py: 1.2, color: '#b22222' }}>
                    ₹{totalSummary.due.toLocaleString()}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

export default Pendingfees;